import { getMapByMissionId, DEFAULT_MAP_ID } from './maps/index.js';
import { SPATIAL_SCALE, getBearingToMapPoint, simulationToMapPoint } from '../../shared/spatial.js';
import type { GameSnapshot, ResourceNodeState, RoomState } from '../../shared/types.js';
import type { MapDefinition } from '../../shared/maps/types.js';
import type { AggregatedConnection, TileJsonNode } from '../../shared/maps/tileJson.js';
import { nodePlanContains, nodePlanCorners } from '../../shared/maps/navShapes.js';
import { buildRevealGraph, parseDiscs, planPassageDraw, type Disc, type PassageDrawPlan, type RevealGraph } from '../../shared/maps/navReveal.js';
import { enclosedTileLayerKeys, heightToDepth, isEnclosedTileKind, localYToDepth } from './navSection.js';
import { sampleMapHeight } from '../../shared/maps/terrainSampling.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BG_COLOUR = '#050810';
const NODE_FILL = '#2a7a7a';
const NODE_RING = '#3db8a8';
const NODE_LABEL_COLOUR = '#6aaba0';
const CONN_STROKE = '#1a3a4a';
/** Passages of enclosed (cave) tiles: solid and clearly lighter than the open-water background. */
const ENCLOSED_CONN_STROKE = '#2f7388';
/** Node/route graphics in open-water tiles are only hints (see navSection.ts for the plan/side rule). */
const OPEN_WATER_GRAPH_ALPHA = 0.4;
/** Seabed relief shading cell size (canvas px). */
const RELIEF_CELL_PX = 16;
const SUB_COLOUR = '#00ffe0';
const WAYPOINT_COLOUR = '#e8b84b';

const CLICK_MOVE_THRESHOLD_PX = 6;
/** A touch this close (canvas px) to the waypoint or tap marker grabs it for dragging instead of panning. */
const MARKER_GRAB_RADIUS_PX = 24;
/** Overlay (pulsing markers) redraw interval: ~30 fps is plenty for a pulse and halves phone load. */
const OVERLAY_FRAME_MS = 33;
const MARKER_COLOUR = '#e06060';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type NodeEntry = {
  node: TileJsonNode;
  tileId: string;
  chunkRegion: string;
  biome: string;
  tempC: number;
  /** Generator kind of the node's tile (open_seabed, cave_maze, ...). See navSection ENCLOSED_TILE_KINDS. */
  tileKind?: string;
};

type ScanTier = 'LOW' | 'MED' | 'HIGH';

// ---------------------------------------------------------------------------
// CaptainNavigation2DMapView
// ---------------------------------------------------------------------------

export class CaptainNavigation2DMapView {
  private readonly container: HTMLElement;
  private readonly mapCanvas: HTMLCanvasElement;
  private readonly overlayCanvas: HTMLCanvasElement;

  // Tile data
  private nodeEntries: NodeEntry[] = [];
  private connections: AggregatedConnection[] = [];
  private chunkSizeXz = 300;

  // Reveal state
  private revealedNodeIds: Set<string> = new Set();
  /** Swept discs decoded from the reveal state, the reveal graph (passage centre lines) and what to draw of each passage. */
  private discs: Disc[] = [];
  private graph: RevealGraph = { nodes: new Map(), connections: [], lines: [] };
  private plans: PassageDrawPlan[] = [];
  private entryByKey = new Map<string, NodeEntry>();

  // View transform
  private panX = 0;
  private panZ = 0;
  private zoom = 1;
  private hasPanned = false;
  /** True until the first snapshot has framed the node the sub starts in. */
  private pendingInitialFit = true;

  // Submarine state
  private subWorldX = 0;
  private subWorldZ = 0;
  private subYawDeg = 0;

  // Waypoint
  private waypointX = 0;
  private waypointZ = 0;
  /** Active mission target in map space; shown even when its node is unrevealed. */
  private missionTarget: { x: number; z: number; depth?: number } | null = null;
  private subDepth = 0;

  // Click marker (placed by single tap; null = no marker)
  private clickMarkerWorldX: number | null = null;
  private clickMarkerWorldZ: number | null = null;

  // Nav waypoint (player-set, shared via server)
  private waypointSetX: number | null = null;
  private waypointSetZ: number | null = null;
  /** Depth (m, positive down) of the shared waypoint when it has one (set from the side view or a room). */
  private waypointDepth: number | undefined = undefined;
  /** Called when the player sets or clears the shared waypoint. Receives world coords (+ depth when known) or null. */
  public onWaypointChange: ((pos: { worldX: number; worldZ: number; depth?: number } | null) => void) | null = null;
  /** Called when the player places, drags or removes the tap marker in this view (map space), so the linked side view can follow. */
  public onMarkerChange: ((marker: { x: number; z: number } | null) => void) | null = null;
  /** Depth of the tap marker, kept in step by the owner when the marker was placed in the side view. */
  public markerDepth: number | undefined = undefined;
  /** What a drag started on: the set waypoint, the tap marker, or nothing (pan). */
  private dragTarget: 'waypoint' | 'marker' | null = null;
  private lastOverlayFrameMs = 0;

  // Node detail popup
  private nodePopup: HTMLElement | null = null;
  private popupNodeKey: string | null = null;

  // Sonar-discovered resource deposits (from the server snapshot).
  private contacts: ResourceNodeState[] = [];
  /** Epoch ms of the last local waypoint edit; snapshots are ignored briefly so a stale one cannot undo it. */
  private waypointChangedAt = 0;

  // Mission HUD
  private activeMissionTitle: string | null = null;
  private activeMissionDescription: string | null = null;

  private activeMap: MapDefinition | null = null;
  /** Footprints (`minX,minZ`) of tiles with any enclosed cave layer: no seabed relief is drawn over them. */
  private enclosedFootprints: Set<string> = new Set();

  // Animation
  private animationFrameId = 0;

  // Pointer state (pan/zoom)
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private lastPinchDist = 0;
  private pointerDownX = 0;
  private pointerDownZ = 0;
  private pointerMoved = false;

  private readonly resizeObserver: ResizeObserver;

  constructor(container: HTMLElement) {
    this.container = container;
    container.style.position = 'relative';
    container.style.overflow = 'hidden';
    container.style.background = BG_COLOUR;

    // Map canvas (nodes, connections)
    this.mapCanvas = document.createElement('canvas');
    this.mapCanvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;';
    container.appendChild(this.mapCanvas);

    // Overlay canvas (sub marker, waypoint)
    this.overlayCanvas = document.createElement('canvas');
    this.overlayCanvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;';
    container.appendChild(this.overlayCanvas);

    // Pointer events on map canvas
    this.mapCanvas.addEventListener('pointerdown', this.onPointerDown);
    this.mapCanvas.addEventListener('pointermove', this.onPointerMove);
    this.mapCanvas.addEventListener('pointerup', this.onPointerUp);
    this.mapCanvas.addEventListener('pointercancel', this.onPointerUp);
    this.mapCanvas.addEventListener('wheel', this.onWheel, { passive: false });

    this.resizeObserver = new ResizeObserver(() => {
      this.syncCanvasSizes();
      this.renderMap();
    });
    this.resizeObserver.observe(container);
    this.syncCanvasSizes();

    this.startOverlayLoop();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  public attachResetButton(target: HTMLElement): void {
    target.addEventListener('click', () => {
      this.hasPanned = false;
      this.panX = 0;
      this.panZ = 0;
      this.zoom = this.defaultZoom();
      this.renderMap();
    });
  }

  /** Re-centre on the submarine at the default zoom and resume auto-follow. */
  public recenter(): void {
    this.hasPanned = false;
    this.panX = this.subWorldX;
    this.panZ = this.subWorldZ;
    this.zoom = this.defaultZoom();
    this.renderMap();
  }

  public hasWaypoint(): boolean {
    return this.waypointSetX !== null;
  }

  /**
   * Set the shared nav waypoint at the tapped marker (or the sub's position when no marker
   * is placed), or clear it if one is already set.
   */
  public toggleWaypoint(): void {
    if (this.waypointSetX !== null) {
      this.clearWaypoint();
      return;
    }
    // Priority: the selected room (its popup is open), then the tapped marker, then the sub itself.
    const selected = this.getSelectedNode();
    if (selected) {
      this.setWaypointAt(selected.node.x, selected.node.z, this.nodeDepth(selected.node));
      return;
    }
    const useMarker = this.clickMarkerWorldX !== null && this.clickMarkerWorldZ !== null;
    this.setWaypointAt(
      useMarker ? (this.clickMarkerWorldX as number) : this.subWorldX,
      useMarker ? (this.clickMarkerWorldZ as number) : this.subWorldZ,
      useMarker ? this.markerDepth : this.subDepth
    );
  }

  /** Depth (m) of a node's centre. Node Y is the tile's local Y plus its layer offset (see controllerRoleControls refreshNavData). */
  private nodeDepth(node: TileJsonNode): number {
    return Math.max(0, localYToDepth(node.y, 0, this.activeMap?.baseDepth ?? 0));
  }

  /** The shared waypoint as this view knows it (map space), or null. */
  public getWaypoint(): { x: number; z: number; depth?: number } | null {
    if (this.waypointSetX === null || this.waypointSetZ === null) return null;
    return { x: this.waypointSetX, z: this.waypointSetZ, ...(this.waypointDepth !== undefined ? { depth: this.waypointDepth } : {}) };
  }

  /** The tap marker (map space), or null. */
  public getMarker(): { x: number; z: number; depth?: number } | null {
    if (this.clickMarkerWorldX === null || this.clickMarkerWorldZ === null) return null;
    return { x: this.clickMarkerWorldX, z: this.clickMarkerWorldZ, ...(this.markerDepth !== undefined ? { depth: this.markerDepth } : {}) };
  }

  /** Place or remove the tap marker from outside (the side view); does not fire onMarkerChange. */
  public setMarkerExternal(marker: { x: number; z: number; depth?: number } | null): void {
    this.clickMarkerWorldX = marker ? marker.x : null;
    this.clickMarkerWorldZ = marker ? marker.z : null;
    this.markerDepth = marker?.depth;
  }

  /** Set the shared waypoint from outside (the side view) and send it to the server. */
  public applyWaypoint(worldX: number, worldZ: number, depth?: number): void {
    this.setWaypointAt(worldX, worldZ, depth);
  }

  /** Clear the shared waypoint from outside. */
  public removeWaypoint(): void {
    this.clearWaypoint();
  }

  /** The node whose detail popup is open (the "selected room"), if any. */
  private getSelectedNode(): NodeEntry | null {
    if (!this.popupNodeKey) return null;
    return this.nodeEntries.find((entry) => `${entry.tileId}:${entry.node.id}` === this.popupNodeKey) ?? null;
  }

  private setWaypointAt(worldX: number, worldZ: number, depth?: number): void {
    this.waypointSetX = worldX;
    this.waypointSetZ = worldZ;
    this.waypointDepth = depth;
    this.clickMarkerWorldX = null;
    this.clickMarkerWorldZ = null;
    this.markerDepth = undefined;
    this.onMarkerChange?.(null);
    this.waypointChangedAt = Date.now();
    this.onWaypointChange?.({ worldX, worldZ, ...(depth !== undefined ? { depth } : {}) });
    this.renderMap();
  }

  private clearWaypoint(): void {
    this.waypointSetX = null;
    this.waypointSetZ = null;
    this.waypointDepth = undefined;
    this.waypointChangedAt = Date.now();
    this.onWaypointChange?.(null);
    this.renderMap();
  }

  public setTileData(nodeEntries: NodeEntry[], connections: AggregatedConnection[]): void {
    this.nodeEntries = nodeEntries;
    this.connections = connections;
    this.entryByKey = new Map(nodeEntries.map((e) => [`${e.tileId}:${e.node.id}`, e]));
    this.graph = buildRevealGraph(nodeEntries, connections);
    this.replanPassages();
    this.refreshEnclosedFootprints();
    // Derive chunk size from the span of all nodes (fallback 300)
    if (nodeEntries.length > 0) {
      let maxExtent = 0;
      for (const { node } of nodeEntries) {
        maxExtent = Math.max(maxExtent, Math.abs(node.x) + node.radius_xz, Math.abs(node.z) + node.radius_xz);
      }
      this.chunkSizeXz = Math.max(maxExtent * 2, 100);
    }
    this.zoom = this.defaultZoom();
    this.renderMap();
  }

  private refreshEnclosedFootprints(): void {
    const map = this.activeMap;
    const tileY = map?.sourceGlbTileSizeMeters?.Y ?? 0;
    const tiles = map?.tileIndex ?? [];
    this.enclosedFootprints = new Set();
    if (!map || tileY <= 0 || tiles.length === 0) return;
    const nodes = this.nodeEntries.map((e) => ({ x: e.node.x, z: e.node.z, depth: this.nodeDepth(e.node), enclosed: isEnclosedTileKind(e.tileKind) }));
    const keys = enclosedTileLayerKeys(tiles, nodes, tileY, map.baseDepth);
    for (const tile of tiles) if (keys.has(tile.key)) this.enclosedFootprints.add(`${tile.minX},${tile.minZ}`);
  }

  public setRevealedNodes(ids: Set<string>): void {
    this.revealedNodeIds = ids;
    this.replanPassages();
    this.renderMap();
  }

  /** Recompute which stretches of each passage to draw (swept discs + discovered ends). Cheap, and only on a reveal change. */
  private replanPassages(): void {
    this.discs = parseDiscs(this.revealedNodeIds);
    this.plans = this.connections.map((conn, i) =>
      planPassageDraw(this.graph.lines[i] ?? [], this.discs, this.revealedNodeIds.has(conn.fromKey), !conn.deadEnd && this.revealedNodeIds.has(conn.toKey), !!conn.deadEnd));
  }

  public getViewState(): { zoom: number; panX: number; panZ: number; hasPanned: boolean } {
    return { zoom: this.zoom, panX: this.panX, panZ: this.panZ, hasPanned: this.hasPanned };
  }

  public restoreViewState(state: { zoom: number; panX: number; panZ: number; hasPanned: boolean }): void {
    this.pendingInitialFit = false;
    this.zoom = state.zoom;
    this.panX = state.panX;
    this.panZ = state.panZ;
    this.hasPanned = state.hasPanned;
  }

  public setRoomState(state: Pick<RoomState, 'mission' | 'missionObjectives' | 'activeMissionIndex'>): void {
    const map = getMapByMissionId(state.mission) ?? getMapByMissionId(DEFAULT_MAP_ID) ?? null;
    this.activeMap = map;
    this.refreshEnclosedFootprints();
    const objective = state.missionObjectives[state.activeMissionIndex] ?? null;
    this.activeMissionTitle = objective?.title ?? null;
    this.activeMissionDescription = objective?.description ?? null;
    this.missionTarget = objective?.location ?? null;
  }

  public setSnapshot(snapshot: GameSnapshot): void {
    this.subWorldX = snapshot.submarine.simulationX * SPATIAL_SCALE.mapXPerSimulationX;
    this.subWorldZ = snapshot.submarine.simulationZ * SPATIAL_SCALE.mapZPerSimulationY;
    this.subYawDeg = snapshot.submarine.yaw;
    this.subDepth = snapshot.submarine.depth;

    // Resource nodes only appear once sonar has discovered them (docs/issues.md).
    this.contacts = (snapshot.world?.resourceNodes ?? []).filter((node) => node.discovered);

    // The shared waypoint lives on the server; mirror it so a re-entered room is correct.
    if (Date.now() - this.waypointChangedAt > 2000) {
      const wp = snapshot.navWaypoint ?? null;
      if (wp) {
        const point = simulationToMapPoint({ x: wp.simulationX, y: wp.simulationZ });
        this.waypointSetX = point.x;
        this.waypointSetZ = point.z;
        this.waypointDepth = wp.depth;
      } else {
        this.waypointSetX = null;
        this.waypointSetZ = null;
        this.waypointDepth = undefined;
      }
    }

    // First view: frame the node the sub is in once data and canvas size are known.
    if (this.pendingInitialFit && this.nodeEntries.length > 0 && this.mapCanvas.width > 0) {
      this.pendingInitialFit = false;
      this.zoom = this.defaultZoom();
    }

    // Auto-follow sub when user hasn't manually panned
    if (!this.hasPanned) {
      this.panX = this.subWorldX;
      this.panZ = this.subWorldZ;
    }
  }

  public dispose(): void {
    cancelAnimationFrame(this.animationFrameId);
    this.resizeObserver.disconnect();
    this.mapCanvas.removeEventListener('pointerdown', this.onPointerDown);
    this.mapCanvas.removeEventListener('pointermove', this.onPointerMove);
    this.mapCanvas.removeEventListener('pointerup', this.onPointerUp);
    this.mapCanvas.removeEventListener('pointercancel', this.onPointerUp);
    this.mapCanvas.removeEventListener('wheel', this.onWheel);
    this.nodePopup?.remove();
    this.nodePopup = null;
  }

  // ---------------------------------------------------------------------------
  // Rendering — map layer
  // ---------------------------------------------------------------------------

  private renderMap(): void {
    const canvas = this.mapCanvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = BG_COLOUR;
    ctx.fillRect(0, 0, W, H);

    // Seabed relief only over open-water tiles (faint), then the node/route graph on top.
    this.renderRelief(ctx, W, H);

    // Draw connections first (behind nodes)
    this.connections.forEach((conn, i) => this.renderConnection(ctx, conn, this.plans[i], W, H));

    // Draw nodes
    for (const { node, tileId } of this.nodeEntries) {
      const key = `${tileId}:${node.id}`;
      if (!this.revealedNodeIds.has(key)) continue;
      const nodeEntry = this.entryByKey.get(key);
      if (!isEnclosedTileKind(nodeEntry?.tileKind)) continue;
      this.renderNode(ctx, node, W, H);
    }
  }

  /**
   * One passage. Where both end chambers are known the whole centre line is drawn; otherwise only the stretches the sonar has swept
   * (src/shared/maps/navReveal.ts) plus a fading hint leaving a known end. Round tubes get round caps, box corridors square ends.
   */
  private renderConnection(
    ctx: CanvasRenderingContext2D,
    conn: AggregatedConnection,
    plan: PassageDrawPlan | undefined,
    W: number,
    H: number
  ): void {
    if (!plan || (plan.solid.length === 0 && plan.stub.length === 0)) return;
    const fromEntry = this.entryByKey.get(conn.fromKey);
    const toEntry = conn.deadEnd ? undefined : this.entryByKey.get(conn.toKey);
    const lineWidth = Math.max(conn.width * this.zoom, 1);
    const enclosed = isEnclosedTileKind(fromEntry?.tileKind) || (!!toEntry && isEnclosedTileKind(toEntry.tileKind));
    const stroke = enclosed ? ENCLOSED_CONN_STROKE : CONN_STROKE;
    const box = conn.shape === 'box';
    ctx.save();
    ctx.globalAlpha = enclosed ? 1 : OPEN_WATER_GRAPH_ALPHA;
    ctx.lineCap = box ? 'butt' : 'round';
    ctx.lineJoin = box ? 'miter' : 'round';
    ctx.lineWidth = lineWidth;

    const trace = (pts: ReadonlyArray<readonly [number, number, number]>): void => {
      ctx.beginPath();
      pts.forEach((p, i) => {
        const { cx, cy } = this.worldToCanvas(p[0], p[2], W, H);
        if (i === 0) ctx.moveTo(cx, cy); else ctx.lineTo(cx, cy);
      });
    };

    ctx.strokeStyle = stroke;
    for (const piece of plan.solid) {
      if (piece.length < 2) continue;
      trace(piece);
      ctx.stroke();
    }
    if (plan.stub.length >= 2) {
      // Fades away from the discovered end.
      const ends = plan.stubFromEnd ? [plan.stub[plan.stub.length - 1], plan.stub[0]] : [plan.stub[0], plan.stub[plan.stub.length - 1]];
      const a = this.worldToCanvas(ends[0][0], ends[0][2], W, H);
      const b = this.worldToCanvas(ends[1][0], ends[1][2], W, H);
      const grad = ctx.createLinearGradient(a.cx, a.cy, b.cx, b.cy);
      grad.addColorStop(0, stroke);
      grad.addColorStop(1, 'transparent');
      ctx.strokeStyle = grad;
      trace(plan.stub);
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * Faint seabed relief (floor depth shading) over OPEN-WATER tiles only. Enclosed cave tiles stay plain so their
   * passages are the one picture there (rule documented in navSection.ts). Cells are sampled on a coarse canvas grid.
   */
  private renderRelief(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    const map = this.activeMap;
    const tiles = map?.tileIndex ?? [];
    if (!map || tiles.length === 0) return; // single-GLB maps have no per-tile kind: keep the plain graph
    const maxDepth = 320;
    for (let py = 0; py < H; py += RELIEF_CELL_PX) {
      for (let px = 0; px < W; px += RELIEF_CELL_PX) {
        const { wx, wz } = this.canvasToWorld(px + RELIEF_CELL_PX / 2, py + RELIEF_CELL_PX / 2, W, H);
        const tile = tiles.find((t) => wx >= t.minX && wx <= t.maxX && wz >= t.minZ && wz <= t.maxZ);
        if (!tile || this.enclosedFootprints.has(`${tile.minX},${tile.minZ}`)) continue;
        const depth = heightToDepth(sampleMapHeight(map, wx, wz), map.baseDepth);
        if (!Number.isFinite(depth)) continue;
        const shallow = 1 - Math.min(1, Math.max(0, depth / maxDepth));
        ctx.fillStyle = `rgba(70,120,150,${(0.035 + shallow * 0.14).toFixed(3)})`;
        ctx.fillRect(px, py, RELIEF_CELL_PX, RELIEF_CELL_PX);
      }
    }
  }

  private renderNode(ctx: CanvasRenderingContext2D, node: TileJsonNode, W: number, H: number): void {
    const { cx, cy } = this.worldToCanvas(node.x, node.z, W, H);
    const r = Math.max(node.radius_xz * this.zoom, 2);

    // Box chambers are drawn as the (rotated) rectangle they are; round caves and tubes as circles.
    const corners = nodePlanCorners(node);
    ctx.beginPath();
    if (corners) {
      corners.forEach(([x, z], i) => {
        const p = this.worldToCanvas(x, z, W, H);
        if (i === 0) ctx.moveTo(p.cx, p.cy); else ctx.lineTo(p.cx, p.cy);
      });
      ctx.closePath();
    } else {
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
    }
    ctx.fillStyle = NODE_FILL;
    ctx.fill();
    ctx.strokeStyle = NODE_RING;
    ctx.lineWidth = Math.max(1, r * 0.06);
    ctx.lineJoin = 'miter';
    ctx.stroke();

    // Label only when large enough
    if (r > 12) {
      const label = node.node_name ?? node.name;
      ctx.font = `10px monospace`;
      ctx.fillStyle = NODE_LABEL_COLOUR;
      ctx.textAlign = 'center';
      const labelDrop = corners ? Math.max((node.half_z ?? node.radius_xz) * this.zoom, 2) : r;
      ctx.fillText(label, cx, cy + labelDrop + 13);
    }
  }

  // ---------------------------------------------------------------------------
  // Rendering — overlay loop
  // ---------------------------------------------------------------------------

  private startOverlayLoop(): void {
    const loop = (now: number): void => {
      if (now - this.lastOverlayFrameMs >= OVERLAY_FRAME_MS) {
        this.lastOverlayFrameMs = now;
        this.renderOverlay();
      }
      this.animationFrameId = requestAnimationFrame(loop);
    };
    this.animationFrameId = requestAnimationFrame(loop);
  }

  private renderOverlay(): void {
    const canvas = this.overlayCanvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    this.drawContacts(ctx, W, H);
    this.drawMissionTarget(ctx, W, H);
    this.drawSubmarine(ctx, W, H);
    this.drawNavWaypoint(ctx, W, H);
    this.drawClickMarker(ctx, W, H);
    this.drawMissionHUD(ctx, W, H);
  }

  /** Sonar-discovered resource deposits. Identified ones show their type and value. */
  private drawContacts(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    if (this.contacts.length === 0) return;
    ctx.save();
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    for (const node of this.contacts) {
      const point = simulationToMapPoint({ x: node.simulationX, y: node.simulationZ });
      const { cx, cy } = this.worldToCanvas(point.x, point.z, W, H);
      if (cx < -20 || cy < -20 || cx > W + 20 || cy > H + 20) continue;
      ctx.beginPath();
      ctx.moveTo(cx, cy - 5);
      ctx.lineTo(cx + 4, cy);
      ctx.lineTo(cx, cy + 5);
      ctx.lineTo(cx - 4, cy);
      ctx.closePath();
      ctx.fillStyle = WAYPOINT_COLOUR;
      if (node.identified) {
        ctx.fill();
        ctx.fillText(`${node.resourceType.toLowerCase()} ${Math.round(node.resourceValue)}`, cx, cy + 16);
      } else {
        ctx.strokeStyle = WAYPOINT_COLOUR;
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  private drawSubmarine(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    const { cx, cy } = this.worldToCanvas(this.subWorldX, this.subWorldZ, W, H);
    const yawRad = ((this.subYawDeg - 90) * Math.PI) / 180;
    const arrowLen = 14;

    ctx.save();
    ctx.translate(cx, cy);

    // Glow
    const glow = ctx.createRadialGradient(0, 0, 2, 0, 0, 10);
    glow.addColorStop(0, 'rgba(0,255,224,0.35)');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, 10, 0, Math.PI * 2);
    ctx.fill();

    // Circle
    ctx.beginPath();
    ctx.arc(0, 0, 5, 0, Math.PI * 2);
    ctx.fillStyle = SUB_COLOUR;
    ctx.fill();

    // Heading arrow
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(yawRad) * arrowLen, Math.sin(yawRad) * arrowLen);
    ctx.strokeStyle = SUB_COLOUR;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.restore();
  }

  private drawMissionTarget(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    if (!this.missionTarget) return;
    const { cx, cy } = this.worldToCanvas(this.missionTarget.x, this.missionTarget.z, W, H);
    const margin = 26;
    if (cx >= margin && cx <= W - margin && cy >= margin && cy <= H - margin) {
      this.drawWaypoint(ctx, W, H, this.missionTarget.x, this.missionTarget.z, `MISSION${this.missionDepthHint()}`);
      return;
    }
    // Off the visible chart: pin an arrow to the edge pointing at the target, with its distance, so the
    // objective is never "missing" just because the map is zoomed in on the submarine.
    const dx = cx - W / 2;
    const dy = cy - H / 2;
    const scale = Math.min((W / 2 - margin) / Math.max(Math.abs(dx), 1e-6), (H / 2 - margin) / Math.max(Math.abs(dy), 1e-6));
    const ex = W / 2 + dx * scale;
    const ey = H / 2 + dy * scale;
    const angle = Math.atan2(dy, dx);
    const distance = Math.hypot(this.missionTarget.x - this.subWorldX, this.missionTarget.z - this.subWorldZ);
    const pulse = 0.8 + 0.2 * Math.sin(Date.now() / 600);

    ctx.save();
    ctx.translate(ex, ey);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(14 * pulse, 0);
    ctx.lineTo(-8, -9 * pulse);
    ctx.lineTo(-3, 0);
    ctx.lineTo(-8, 9 * pulse);
    ctx.closePath();
    ctx.fillStyle = WAYPOINT_COLOUR;
    ctx.fill();
    ctx.strokeStyle = '#fff9e0';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.font = '10px monospace';
    ctx.fillStyle = WAYPOINT_COLOUR;
    ctx.textAlign = ex > W / 2 ? 'right' : 'left';
    ctx.textBaseline = 'middle';
    const labelX = ex + (ex > W / 2 ? -20 : 20);
    const labelY = Math.min(Math.max(ey, 14), H - 14);
    ctx.fillText(`MISSION ${Math.round(distance)} m${this.missionDepthHint()}`, labelX, labelY);
    ctx.restore();
  }

  /** " ▼14 m" / " ▲6 m": how far below / above the mission target the sub is (reaching it is judged in 3D). */
  private missionDepthHint(): string {
    return this.depthHint(this.missionTarget?.depth);
  }

  private depthHint(targetDepth: number | undefined): string {
    if (targetDepth === undefined) return '';
    const delta = targetDepth - this.subDepth;
    if (Math.abs(delta) < 3) return '';
    return ` ${delta > 0 ? '▼' : '▲'}${Math.round(Math.abs(delta))} m`;
  }

  private drawWaypoint(ctx: CanvasRenderingContext2D, W: number, H: number, wx: number, wz: number, label = 'MISSION'): void {
    const { cx, cy } = this.worldToCanvas(wx, wz, W, H);

    // Pulsing scale
    const pulse = 0.75 + 0.25 * Math.sin(Date.now() / 600);
    const s = 8 * pulse;

    ctx.save();
    ctx.translate(cx, cy);

    // Glow
    const glow = ctx.createRadialGradient(0, 0, s * 0.5, 0, 0, s * 2.5);
    glow.addColorStop(0, 'rgba(232,184,75,0.4)');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, s * 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Diamond
    ctx.beginPath();
    ctx.moveTo(0, -s);
    ctx.lineTo(s, 0);
    ctx.lineTo(0, s);
    ctx.lineTo(-s, 0);
    ctx.closePath();
    ctx.fillStyle = WAYPOINT_COLOUR;
    ctx.fill();
    ctx.strokeStyle = '#fff9e0';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.font = '9px monospace';
    ctx.fillStyle = WAYPOINT_COLOUR;
    ctx.textAlign = 'center';
    ctx.fillText(label, 0, s + 12);

    ctx.restore();
  }

  private drawNavWaypoint(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    if (this.waypointSetX === null || this.waypointSetZ === null) return;

    const { cx, cy } = this.worldToCanvas(this.waypointSetX, this.waypointSetZ, W, H);
    const pulse = 0.75 + 0.25 * Math.sin(Date.now() / 500);
    const s = 9 * pulse;

    ctx.save();
    ctx.translate(cx, cy);

    // Glow
    const glow = ctx.createRadialGradient(0, 0, s * 0.5, 0, 0, s * 2.5);
    glow.addColorStop(0, 'rgba(220,60,60,0.45)');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, s * 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Red diamond
    ctx.beginPath();
    ctx.moveTo(0, -s);
    ctx.lineTo(s, 0);
    ctx.lineTo(0, s);
    ctx.lineTo(-s, 0);
    ctx.closePath();
    ctx.fillStyle = '#dc3c3c';
    ctx.fill();
    ctx.strokeStyle = '#ffcccc';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.font = '9px monospace';
    ctx.fillStyle = '#dc3c3c';
    ctx.textAlign = 'center';
    ctx.fillText(`WAYPOINT${this.depthHint(this.waypointDepth)}`, 0, s + 12);

    ctx.restore();
  }

  private drawClickMarker(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    if (this.clickMarkerWorldX === null || this.clickMarkerWorldZ === null) return;

    const { cx: mx, cy: my } = this.worldToCanvas(this.clickMarkerWorldX, this.clickMarkerWorldZ, W, H);
    const { cx: sx, cy: sy } = this.worldToCanvas(this.subWorldX, this.subWorldZ, W, H);

    ctx.save();

    // Dashed line from sub to marker
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = 'rgba(224,96,96,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(mx, my);
    ctx.stroke();
    ctx.setLineDash([]);

    // Pulsing concentric rings
    const t = Date.now() / 800;
    for (let i = 0; i < 3; i++) {
      const phase = (t + i / 3) % 1;
      const r = 6 + phase * 16;
      const alpha = 1 - phase;
      ctx.beginPath();
      ctx.arc(mx, my, r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(224,96,96,${alpha.toFixed(2)})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Centre dot
    ctx.beginPath();
    ctx.arc(mx, my, 3, 0, Math.PI * 2);
    ctx.fillStyle = MARKER_COLOUR;
    ctx.fill();

    // Bearing and distance label
    const dx = this.clickMarkerWorldX - this.subWorldX;
    const dz = this.clickMarkerWorldZ - this.subWorldZ;
    const distMapUnits = Math.sqrt(dx * dx + dz * dz);
    const distSim = distMapUnits / SPATIAL_SCALE.mapXPerSimulationX;
    const bearingDeg = getBearingToMapPoint({ x: this.subWorldX, z: this.subWorldZ }, { x: this.clickMarkerWorldX, z: this.clickMarkerWorldZ });

    const label1 = `BRG ${bearingDeg.toFixed(0).padStart(3, '0')}°`;
    const label2 = `DST ${distSim.toFixed(0)}m`;

    ctx.font = 'bold 10px monospace';
    ctx.textAlign = 'center';
    const labelY = my + 24;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(mx - 32, labelY - 11, 64, 26);
    ctx.fillStyle = MARKER_COLOUR;
    ctx.fillText(label1, mx, labelY);
    ctx.fillText(label2, mx, labelY + 13);

    ctx.restore();
  }

  private drawMissionHUD(ctx: CanvasRenderingContext2D, W: number, _H: number): void {
    if (!this.activeMissionTitle) return;
    const x = 12;
    let y = 20;
    ctx.font = 'bold 11px monospace';
    ctx.fillStyle = '#00c8b0';
    ctx.textAlign = 'left';
    ctx.fillText(this.activeMissionTitle, x, y);
    if (this.activeMissionDescription) {
      y += 14;
      ctx.font = '10px monospace';
      ctx.fillStyle = '#6aaba0';
      // Wrap at ~W-24 px
      const maxW = W - 24;
      const words = this.activeMissionDescription.split(' ');
      let line = '';
      for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (ctx.measureText(test).width > maxW && line) {
          ctx.fillText(line, x, y);
          y += 13;
          line = word;
        } else {
          line = test;
        }
      }
      if (line) ctx.fillText(line, x, y);
    }
  }

  // ---------------------------------------------------------------------------
  // Node detail popup
  // ---------------------------------------------------------------------------

  private dismissNodePopup(): void {
    this.nodePopup?.remove();
    this.nodePopup = null;
    this.popupNodeKey = null;
  }

  private showNodePopup(entry: NodeEntry, canvasX: number, canvasY: number, W: number, H: number): void {
    this.dismissNodePopup();

    this.popupNodeKey = `${entry.tileId}:${entry.node.id}`;

    const popup = document.createElement('div');
    popup.style.cssText = [
      'position:absolute',
      'z-index:10',
      'background:#08111e',
      'border:1px solid #1a4060',
      'border-radius:6px',
      'padding:8px 10px 10px',
      'width:210px',
      'font-family:monospace',
      'font-size:10px',
      'color:#7ab0c0',
      'pointer-events:auto',
      'box-shadow:0 4px 16px rgba(0,0,0,0.7)',
      'line-height:1.4'
    ].join(';');

    this.buildPopupContent(popup, entry);

    // "Set waypoint" straight from the room's popup (or clear it if the waypoint is already here).
    const waypointBtn = document.createElement('button');
    const atThisRoom = this.waypointSetX !== null && this.waypointSetZ !== null
      && Math.hypot(this.waypointSetX - entry.node.x, this.waypointSetZ - entry.node.z) < 1;
    waypointBtn.textContent = atThisRoom ? 'Clear waypoint' : 'Set waypoint here';
    waypointBtn.style.cssText = 'display:block;width:100%;margin-top:8px;padding:6px 8px;background:rgba(232,184,75,0.14);border:1px solid #e8b84b;border-radius:4px;color:#e8b84b;font:700 10px monospace;letter-spacing:0.08em;text-transform:uppercase;cursor:pointer;';
    waypointBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      if (atThisRoom) this.clearWaypoint();
      else this.setWaypointAt(entry.node.x, entry.node.z, this.nodeDepth(entry.node));
      this.dismissNodePopup();
    });
    popup.appendChild(waypointBtn);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = 'position:absolute;top:5px;right:7px;background:none;border:none;color:#4a7080;cursor:pointer;font-size:14px;line-height:1;padding:0;';
    closeBtn.addEventListener('click', () => this.dismissNodePopup());
    popup.appendChild(closeBtn);

    // Position popup near the tap, clamped within the container
    const cW = this.container.clientWidth;
    const cH = this.container.clientHeight;
    const popupW = 218;
    const estPopupH = 260;
    const dispX = canvasX * (cW / W);
    const dispY = canvasY * (cH / H);

    let left = dispX < cW / 2 ? dispX + 14 : dispX - popupW - 14;
    let top = dispY < cH / 2 ? dispY + 14 : dispY - estPopupH - 14;
    left = Math.max(4, Math.min(left, cW - popupW - 4));
    top = Math.max(4, Math.min(top, cH - estPopupH - 4));

    popup.style.left = `${left}px`;
    popup.style.top = `${top}px`;

    this.container.appendChild(popup);
    this.nodePopup = popup;
  }

  private buildPopupContent(popup: HTMLElement, entry: NodeEntry): void {
    const node = entry.node;
    const dist = Math.hypot(this.subWorldX - node.x, this.subWorldZ - node.z);
    const tier = navScanTier(dist, node.radius_xz);

    // ── Header ──────────────────────────────────────────────────────────────
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:baseline;gap:6px;margin-bottom:5px;padding-right:18px;';

    const nameEl = document.createElement('div');
    nameEl.style.cssText = 'color:#d0e8e8;font-weight:bold;font-size:11px;word-break:break-word;flex:1;';
    if (tier === 'LOW') {
      nameEl.textContent = `${entry.chunkRegion} UNKNOWN`;
    } else {
      nameEl.textContent = node.node_name
        ? `${entry.chunkRegion} ${node.node_name}`
        : node.name;
    }

    const tierEl = document.createElement('div');
    const tierColors: Record<ScanTier, string> = { HIGH: '#204a28,#40d060', MED: '#4a3a00,#c0a000', LOW: '#4a1010,#c04040' };
    const [tierBg, tierFg] = tierColors[tier].split(',');
    tierEl.style.cssText = `flex-shrink:0;padding:1px 5px;border-radius:3px;font-size:9px;font-weight:bold;background:${tierBg};color:${tierFg};border:1px solid ${tierFg};`;
    tierEl.textContent = tier;

    header.append(nameEl, tierEl);
    popup.appendChild(header);

    // ── Bearing / distance ────────────────────────────────────────────────
    const brgDstEl = document.createElement('div');
    brgDstEl.style.cssText = 'color:#4a7a88;font-size:9px;margin-bottom:7px;';
    const bearingDeg = getBearingToMapPoint({ x: this.subWorldX, z: this.subWorldZ }, { x: node.x, z: node.z });
    const distSim = dist / SPATIAL_SCALE.mapXPerSimulationX;
    brgDstEl.textContent = `BRG ${bearingDeg.toFixed(0).padStart(3, '0')}°   DST ${distSim.toFixed(0)} m`;
    popup.appendChild(brgDstEl);

    const hr = (): HTMLElement => {
      const d = document.createElement('div');
      d.style.cssText = 'border-top:1px solid #122030;margin:5px 0 4px;';
      return d;
    };
    const sectionLabel = (text: string): HTMLElement => {
      const d = document.createElement('div');
      d.style.cssText = 'color:#2a7090;font-size:9px;font-weight:bold;letter-spacing:0.05em;margin-bottom:3px;';
      d.textContent = text;
      return d;
    };
    const row = (label: string, value: string | null | undefined, note?: string): HTMLElement => {
      const d = document.createElement('div');
      d.style.cssText = 'display:flex;justify-content:space-between;gap:4px;';
      const lEl = document.createElement('span');
      lEl.style.cssText = 'color:#4a7a88;';
      lEl.textContent = label;
      const vEl = document.createElement('span');
      vEl.style.cssText = `color:${value ? '#a0c8d0' : '#2a4a54'};text-align:right;`;
      if (!value) {
        vEl.textContent = '—';
      } else if (note) {
        vEl.innerHTML = `${value} <span style="color:#4a7a50;font-size:9px;">${note}</span>`;
      } else {
        vEl.textContent = value;
      }
      d.append(lEl, vEl);
      return d;
    };

    // ── Environmental ──────────────────────────────────────────────────────
    popup.appendChild(hr());
    popup.appendChild(sectionLabel('ENVIRONMENTAL'));

    popup.appendChild(row('biome', tier === 'LOW' ? null : entry.biome));
    if (tier === 'HIGH') popup.appendChild(row('temp', `${entry.tempC}°C`));
    if (tier === 'HIGH') popup.appendChild(row('size', navSizeLabel(node.radius_xz)));
    popup.appendChild(row('life', tier === 'LOW' ? null : (node.life ?? 'none')));
    if (tier === 'HIGH') popup.appendChild(row('visibility', node.visibility ?? null));
    if (tier !== 'LOW') {
      const commsVal = !node.communications || node.communications === 'none'
        ? 'none'
        : tier === 'MED' ? 'detected' : node.communications;
      popup.appendChild(row('comms', commsVal));
    }

    // ── Resources ─────────────────────────────────────────────────────────
    popup.appendChild(hr());
    popup.appendChild(sectionLabel('RESOURCES'));

    type RichMarker = (typeof node.markers)[number] & { rarity?: string | null; object_type?: string | null };
    const markers = node.markers as RichMarker[];

    // Sonar-scanned deposits inside this node override the estimate with what was actually found.
    const scanned = this.contacts.filter((c) => {
      const p = simulationToMapPoint({ x: c.simulationX, y: c.simulationZ });
      return nodePlanContains(node, p.x, p.z);
    });
    if (scanned.length > 0) {
      const found = new Map<string, number>();
      let unidentified = 0;
      for (const c of scanned) {
        if (c.identified) found.set(c.resourceType, (found.get(c.resourceType) ?? 0) + c.resourceValue);
        else unidentified += 1;
      }
      for (const [type, val] of found) popup.appendChild(row(type.toLowerCase(), String(Math.round(val)), 'scanned'));
      if (unidentified > 0) popup.appendChild(row('unidentified', String(unidentified), 'scanned'));
      popup.appendChild(row('base', node.attributes.includes('base') ? 'yes' : 'no'));
      return;
    }

    if (tier === 'LOW') {
      popup.appendChild(row('quantity', null));
      popup.appendChild(row('base', null));
      popup.appendChild(row('objects', null));
    } else if (tier === 'MED') {
      const resMks = markers.filter(m => m.resourceType);
      const totalVal = resMks.reduce((s, m) => s + (m.resourceValue ?? 0), 0);
      const qty = totalVal > 100 ? 'rich' : totalVal > 30 ? 'moderate' : resMks.length > 0 ? 'sparse' : 'none';
      const hasRare = resMks.some(m => m.rarity === 'rare');
      const hasUncommon = resMks.some(m => m.rarity === 'uncommon');
      const qual = hasRare ? 'good' : hasUncommon ? 'average' : resMks.length > 0 ? 'poor' : null;
      const hasBase = node.attributes.includes('base');
      const objCount = markers.filter(m => m.object_type).length;

      popup.appendChild(row('quantity', qty !== 'none' ? qty : null, qty !== 'none' ? 'est' : undefined));
      if (qual) popup.appendChild(row('quality', qual, 'est'));
      popup.appendChild(row('base', hasBase ? 'yes' : 'no'));
      popup.appendChild(row('objects', objCount > 0 ? `${objCount} detected` : 'none'));
    } else {
      // HIGH
      const resMap = new Map<string, number>();
      for (const m of markers) {
        if (m.resourceType) {
          resMap.set(m.resourceType, (resMap.get(m.resourceType) ?? 0) + (m.resourceValue ?? 0));
        }
      }
      const rarities: Record<string, number> = {};
      for (const m of markers) {
        if (!m.resourceType && m.rarity) {
          rarities[m.rarity] = (rarities[m.rarity] ?? 0) + 1;
        }
      }
      if (resMap.size > 0) {
        for (const [type, val] of resMap) {
          popup.appendChild(row(type.toLowerCase(), String(Math.round(val))));
        }
      }
      for (const [bucket, cnt] of Object.entries(rarities)) {
        popup.appendChild(row(bucket, String(cnt)));
      }
      if (resMap.size === 0 && Object.keys(rarities).length === 0) {
        popup.appendChild(row('deposits', 'none'));
      }
      const hasBase = node.attributes.includes('base');
      popup.appendChild(row('base', hasBase ? 'yes' : 'no'));
      const objTypes = [...new Set(markers.filter(m => m.object_type).map(m => m.object_type as string))];
      popup.appendChild(row('objects', objTypes.length > 0 ? objTypes.join(', ') : 'none'));
    }
  }

  // ---------------------------------------------------------------------------
  // Coordinate helpers
  // ---------------------------------------------------------------------------

  private worldToCanvas(wx: number, wz: number, W: number, H: number): { cx: number; cy: number } {
    return {
      cx: (wx - this.panX) * this.zoom + W / 2,
      cy: (wz - this.panZ) * this.zoom + H / 2
    };
  }

  private canvasToWorld(cx: number, cy: number, W: number, H: number): { wx: number; wz: number } {
    return {
      wx: (cx - W / 2) / this.zoom + this.panX,
      wz: (cy - H / 2) / this.zoom + this.panZ
    };
  }

  private defaultZoom(): number {
    const W = this.mapCanvas.width || 400;
    const H = this.mapCanvas.height || 400;
    // Fit the node the sub is in to two thirds of the view.
    let nodeRadius = 0;
    for (const { node } of this.nodeEntries) {
      if (nodePlanContains(node, this.subWorldX, this.subWorldZ) && (nodeRadius === 0 || node.radius_xz < nodeRadius)) {
        nodeRadius = node.radius_xz;
      }
    }
    if (nodeRadius > 0) {
      return (Math.min(W, H) * (2 / 3)) / (nodeRadius * 2);
    }
    return Math.min(W, H) / (this.chunkSizeXz * 1.1);
  }

  // ---------------------------------------------------------------------------
  // Pointer / wheel handlers
  // ---------------------------------------------------------------------------

  /** Which draggable mark (set waypoint, else tap marker) is under a canvas-space point, if any. */
  private hitDraggable(canvasX: number, canvasY: number): 'waypoint' | 'marker' | null {
    const W = this.mapCanvas.width;
    const H = this.mapCanvas.height;
    if (this.waypointSetX !== null && this.waypointSetZ !== null) {
      const p = this.worldToCanvas(this.waypointSetX, this.waypointSetZ, W, H);
      if (Math.hypot(canvasX - p.cx, canvasY - p.cy) <= MARKER_GRAB_RADIUS_PX) return 'waypoint';
    }
    if (this.clickMarkerWorldX !== null && this.clickMarkerWorldZ !== null) {
      const p = this.worldToCanvas(this.clickMarkerWorldX, this.clickMarkerWorldZ, W, H);
      if (Math.hypot(canvasX - p.cx, canvasY - p.cy) <= MARKER_GRAB_RADIUS_PX) return 'marker';
    }
    return null;
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    this.mapCanvas.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.pointerDownX = e.clientX;
    this.pointerDownZ = e.clientY;
    this.pointerMoved = false;
    this.dragTarget = null;
    if (this.pointers.size === 1) {
      const rect = this.mapCanvas.getBoundingClientRect();
      const canvasX = (e.clientX - rect.left) * (this.mapCanvas.width / rect.width);
      const canvasY = (e.clientY - rect.top) * (this.mapCanvas.height / rect.height);
      this.dragTarget = this.hitDraggable(canvasX, canvasY);
    }
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    const prev = this.pointers.get(e.pointerId)!;

    if (this.pointers.size === 1 && this.dragTarget && (this.pointerMoved || Math.hypot(e.clientX - this.pointerDownX, e.clientY - this.pointerDownZ) > CLICK_MOVE_THRESHOLD_PX)) {
      // Drag the grabbed waypoint / marker; the linked side view follows via the callbacks on release / move.
      const rect = this.mapCanvas.getBoundingClientRect();
      const canvasX = (e.clientX - rect.left) * (this.mapCanvas.width / rect.width);
      const canvasY = (e.clientY - rect.top) * (this.mapCanvas.height / rect.height);
      const { wx, wz } = this.canvasToWorld(canvasX, canvasY, this.mapCanvas.width, this.mapCanvas.height);
      if (this.dragTarget === 'waypoint') {
        this.waypointSetX = wx;
        this.waypointSetZ = wz;
        this.waypointChangedAt = Date.now();
      } else {
        this.clickMarkerWorldX = wx;
        this.clickMarkerWorldZ = wz;
        this.onMarkerChange?.({ x: wx, z: wz });
      }
      this.pointerMoved = true;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      return;
    }

    if (this.pointers.size === 1) {
      // Pan
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      const rect = this.mapCanvas.getBoundingClientRect();
      const scaleX = this.mapCanvas.width / rect.width;
      const scaleY = this.mapCanvas.height / rect.height;
      this.panX -= (dx * scaleX) / this.zoom;
      this.panZ -= (dy * scaleY) / this.zoom;
      this.hasPanned = true;
      this.renderMap();
    } else if (this.pointers.size === 2) {
      // Pinch-zoom
      const ids = [...this.pointers.keys()];
      const other = ids.find(id => id !== e.pointerId)!;
      const otherPos = this.pointers.get(other)!;
      const newDist = Math.hypot(e.clientX - otherPos.x, e.clientY - otherPos.y);
      if (this.lastPinchDist > 0) {
        const factor = newDist / this.lastPinchDist;
        this.applyZoom(factor, (e.clientX + otherPos.x) / 2, (e.clientY + otherPos.y) / 2);
      }
      this.lastPinchDist = newDist;
      this.hasPanned = true;
    }

    if (
      Math.abs(e.clientX - this.pointerDownX) > CLICK_MOVE_THRESHOLD_PX ||
      Math.abs(e.clientY - this.pointerDownZ) > CLICK_MOVE_THRESHOLD_PX
    ) {
      this.pointerMoved = true;
    }

    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    const wasSinglePointer = this.pointers.size === 1;
    this.pointers.delete(e.pointerId);
    this.lastPinchDist = 0;

    if (this.dragTarget) {
      const dragged = this.dragTarget;
      this.dragTarget = null;
      if (this.pointerMoved) {
        // Finish the drag: a waypoint is re-sent to the server (keeping its depth), a marker just stays put.
        if (dragged === 'waypoint' && this.waypointSetX !== null && this.waypointSetZ !== null) {
          this.waypointChangedAt = Date.now();
          this.onWaypointChange?.({ worldX: this.waypointSetX, worldZ: this.waypointSetZ, ...(this.waypointDepth !== undefined ? { depth: this.waypointDepth } : {}) });
        }
        return;
      }
    }

    if (wasSinglePointer && !this.pointerMoved) {
      const rect = this.mapCanvas.getBoundingClientRect();
      const scaleX = this.mapCanvas.width / rect.width;
      const scaleY = this.mapCanvas.height / rect.height;
      const canvasX = (e.clientX - rect.left) * scaleX;
      const canvasY = (e.clientY - rect.top) * scaleY;
      const W = this.mapCanvas.width;
      const H = this.mapCanvas.height;

      // Check whether the tap lands on a revealed node (circle or label area).
      // Prefer the node whose canvas centre is closest to the tap.
      let hitEntry: NodeEntry | null = null;
      let hitDist = Infinity;
      for (const entry of this.nodeEntries) {
        const key = `${entry.tileId}:${entry.node.id}`;
        if (!this.revealedNodeIds.has(key)) continue;
        const { cx, cy } = this.worldToCanvas(entry.node.x, entry.node.z, W, H);
        const r = Math.max(entry.node.radius_xz * this.zoom, 2);
        // Hit area: circle + label strip below (touch-friendly: +14px buffer)
        const dx = canvasX - cx;
        const dy = canvasY - cy;
        const touchR = r + 14;
        // Also cover label: extend hit area downward by label height
        const inCircle = dx * dx + dy * dy <= touchR * touchR;
        const inLabel = r > 12 && Math.abs(dx) < 40 && dy > r && dy < r + 28;
        if (inCircle || inLabel) {
          const d = Math.hypot(dx, dy);
          if (d < hitDist) {
            hitDist = d;
            hitEntry = entry;
          }
        }
      }

      if (hitEntry) {
        const key = `${hitEntry.tileId}:${hitEntry.node.id}`;
        if (this.popupNodeKey === key) {
          // Tapping the same node toggles the popup off
          this.dismissNodePopup();
        } else {
          this.showNodePopup(hitEntry, canvasX, canvasY, W, H);
        }
        return;
      }

      // Tap landed on empty space — dismiss popup if open
      if (this.nodePopup) {
        this.dismissNodePopup();
        return;
      }

      // No popup involved — place or dismiss the bearing/distance marker
      const { wx, wz } = this.canvasToWorld(canvasX, canvasY, W, H);
      if (this.clickMarkerWorldX !== null && this.clickMarkerWorldZ !== null) {
        const { cx: mx, cy: mz } = this.worldToCanvas(this.clickMarkerWorldX, this.clickMarkerWorldZ, W, H);
        if (Math.hypot(canvasX - mx, canvasY - mz) < 20) {
          this.clickMarkerWorldX = null;
          this.clickMarkerWorldZ = null;
          this.markerDepth = undefined;
          this.onMarkerChange?.(null);
          return;
        }
      }
      this.clickMarkerWorldX = wx;
      this.clickMarkerWorldZ = wz;
      this.markerDepth = undefined;
      this.onMarkerChange?.({ x: wx, z: wz });
    }
  };

  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    this.applyZoom(factor, e.clientX, e.clientY);
    this.hasPanned = true;
  };

  private applyZoom(factor: number, clientX: number, clientY: number): void {
    const rect = this.mapCanvas.getBoundingClientRect();
    const scaleX = this.mapCanvas.width / rect.width;
    const scaleY = this.mapCanvas.height / rect.height;
    const canvasX = (clientX - rect.left) * scaleX;
    const canvasY = (clientY - rect.top) * scaleY;
    const W = this.mapCanvas.width;
    const H = this.mapCanvas.height;

    const { wx, wz } = this.canvasToWorld(canvasX, canvasY, W, H);
    const newZoom = Math.max(0.3, Math.min(this.zoom * factor, 20));
    this.zoom = newZoom;
    // Adjust pan so the world point under the cursor stays fixed
    this.panX = wx - (canvasX - W / 2) / this.zoom;
    this.panZ = wz - (canvasY - H / 2) / this.zoom;
    this.renderMap();
  }

  // ---------------------------------------------------------------------------
  // Utility
  // ---------------------------------------------------------------------------

  private syncCanvasSizes(): void {
    const W = this.container.clientWidth || 400;
    const H = this.container.clientHeight || 400;
    for (const canvas of [this.mapCanvas, this.overlayCanvas]) {
      if (canvas.width !== W || canvas.height !== H) {
        canvas.width = W;
        canvas.height = H;
      }
    }
    if (this.zoom <= 0) {
      this.zoom = this.defaultZoom();
    }
  }
}

// ---------------------------------------------------------------------------
// Module-level helpers
// ---------------------------------------------------------------------------

/** Determine scan tier from sub distance (world units) to node centre. */
function navScanTier(dist: number, radius: number): ScanTier {
  if (dist <= radius) return 'HIGH';
  // MED: up to 100 world units (= 10 sim units) outside the node boundary
  if (dist <= radius + 100) return 'MED';
  return 'LOW';
}

/** Derive a human-readable size label from a node's radius_xz (world units). */
function navSizeLabel(radius: number): string {
  if (radius < 10) return 'small';
  if (radius < 22) return 'medium';
  if (radius < 50) return 'large';
  return 'vast';
}
